import { useEffect, useState } from "react";
import { phCapture } from "@/lib/posthog";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Circle, Loader2, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import RequireAuth from "@/components/RequireAuth";

const STEPS = [
  "Validate links",
  "Extract review text",
  "Identify themes & objections",
  "Generate copy pack",
];

const Processing = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [status, setStatus] = useState<string>("pending");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [priceRadarEnabled, setPriceRadarEnabled] = useState(false);
  const [elapsedOver3Min, setElapsedOver3Min] = useState(false);

  // Fetch price_radar_enabled on mount
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("reports")
        .select("price_radar_enabled")
        .eq("id", id)
        .single();
      if (data?.price_radar_enabled) setPriceRadarEnabled(true);
    })();
  }, [id]);

  // 3-minute timer for "still working" message
  useEffect(() => {
    if (!priceRadarEnabled || status !== "pending") return;
    const timer = setTimeout(() => setElapsedOver3Min(true), 180_000);
    return () => clearTimeout(timer);
  }, [priceRadarEnabled, status]);

  // Animate steps visually on a timer
  useEffect(() => {
    if (status !== "pending") return;
    if (currentStep >= STEPS.length) return;
    const timer = setTimeout(() => {
      setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
    }, 800 + Math.random() * 400);
    return () => clearTimeout(timer);
  }, [currentStep, status]);

  // Poll Supabase every 2s
  useEffect(() => {
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from("reports")
        .select("status, error_message")
        .eq("id", id)
        .single();

      if (data) {
        if (data.status === "complete") {
          setStatus("complete");
          setCurrentStep(STEPS.length);
          clearInterval(interval);
          phCapture("report_generation_succeeded", { report_id: id });
          setTimeout(() => navigate(`/report/${id}`, { replace: true }), 600);
        } else if (data.status === "failed") {
          setStatus("failed");
          setErrorMessage(data.error_message);
          clearInterval(interval);
          phCapture("report_generation_failed", { report_id: id });
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [id, navigate]);

  const progress = status === "complete"
    ? 100
    : Math.round(((currentStep + 1) / STEPS.length) * 90);

  return (
    <RequireAuth>
    <Layout>
      <div className="container mx-auto max-w-md px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Generating your Copy Pack…</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground">PDF is generated when you download — no waiting here.</p>

            <ul className="space-y-3">
              {STEPS.map((step, i) => {
                const done = status === "complete" ? true : i < currentStep;
                const active = status === "pending" && i === currentStep;
                return (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    {done ? (
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                    ) : active ? (
                      <Loader2 className="h-5 w-5 text-primary shrink-0 animate-spin" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground/40 shrink-0" />
                    )}
                    <span className={done ? "text-foreground" : active ? "text-foreground font-medium" : "text-muted-foreground"}>
                      {step}
                    </span>
                  </li>
                );
              })}
            </ul>

            {priceRadarEnabled && status === "pending" && (
              <div className="rounded-md border border-border bg-muted/50 p-3 space-y-1">
                <p className="text-sm text-muted-foreground">
                  Pricing scan can take 1–3 minutes depending on sources.
                </p>
                {elapsedOver3Min && (
                  <p className="text-sm font-medium text-foreground">
                    Still working — please keep this tab open.
                  </p>
                )}
              </div>
            )}

            {status === "failed" && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{errorMessage || "Something went wrong."}</p>
                </div>
                <Button asChild variant="outline" size="sm">
                  <Link to="/generate">Try Again</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
    </RequireAuth>
  );
};

export default Processing;
